import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useCampaign } from "@/contexts/CampaignContext";
import { PageContainer } from "@/components/PageContainer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Phone, AtSign, Building2, User } from "lucide-react";
import { getUserProfile } from "@/api/profile";
import { useToast } from "@/hooks/useToast";

export function ContactInfo() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { contactInfo, setContactInfo } = useCampaign();
  const { register, handleSubmit, reset } = useForm({
    defaultValues: contactInfo || {
      agencyName: "",
      contactName: "",
      contactEmail: "",
      contactPhone: ""
    }
  });

  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const profile = await getUserProfile();
        // Only prefill if contact info is not already set
        if (!contactInfo) {
          reset({
            agencyName: profile.agencyName || "",
            contactName: profile.agencyContactName || "",
            contactEmail: profile.email || "",
            contactPhone: profile.agencyContactPhone || ""
          });
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive",
        });
      }
    };

    fetchProfileData();
  }, [reset, contactInfo, toast]);

  const onSubmit = (data) => {
    setContactInfo(data);
    navigate("/create/overview");
  };

  return (
    <PageContainer>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CardHeader>
          <CardTitle>Contact Information</CardTitle>
          <CardDescription>
            Please provide your agency and contact details
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="agencyName">Agency Name</Label>
            <div className="relative">
              <Building2 className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="agencyName"
                className="pl-10"
                {...register("agencyName", { required: true })}
                placeholder="Enter agency name"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="contactName">Contact Name</Label>
            <div className="relative">
              <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="contactName"
                className="pl-10"
                {...register("contactName", { required: true })}
                placeholder="Enter contact name"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="contactEmail">Contact Email</Label>
            <div className="relative">
              <AtSign className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="contactEmail"
                type="email"
                className="pl-10"
                {...register("contactEmail", { required: true })}
                placeholder="Enter contact email"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="contactPhone">Contact Phone</Label>
            <div className="relative">
              <Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="contactPhone"
                type="tel"
                className="pl-10"
                {...register("contactPhone", { required: true })}
                placeholder="Enter contact phone"
              />
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button type="button" variant="outline" onClick={() => navigate("/create/creative")}>
            Back
          </Button>
          <Button type="submit">Next: Review</Button>
        </CardFooter>
      </form>
    </PageContainer>
  );
}